import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { tokenResponse } from '../models/global.model';
import { environment } from 'src/environments/environment';
import { WebViewInteropService } from './web-view-interop.service';
import { ConfigService } from './config.service';
import { EventTypes, LoginResponse, OidcSecurityService, PublicEventsService } from 'angular-auth-oidc-client';
import { concatMap, filter, forkJoin, map, Observable, take } from 'rxjs';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root'
})
/*
This service will manage the authentication for this app.
It can be used in different components as injection. So other components can access these functions and data.
 */
export class AuthService {
  endPointUri = environment.apiUri + environment.acsConfQuery + environment.apiCode;
  private readonly eventService = inject(PublicEventsService);
  private missingAuthentication: string[] = [];
  constructor(private authService: OidcSecurityService, private router: Router, private http: HttpClient, private interop: WebViewInteropService, private configService: ConfigService, private logger: LoggerService) {
    this.authService.checkAuthMultiple()
      .subscribe(async (loginResponse: LoginResponse[]) => {
        this.missingAuthentication = loginResponse.filter(r => !r.isAuthenticated).map(r => r.configId!);
        console.log('LoginResponse', loginResponse);
        console.log('Missing Authentication (checkAuthMultiple):', this.missingAuthentication);
      });

    this.eventService.registerForEvents()
      .pipe(filter((notification) => notification.type === EventTypes.SilentRenewFailed))
      .subscribe(async (value) => {
        this.logger.log('SilentRenewFailed with value', value);
        //this.checkLogin();
      });
  }

  async checkLogin() {
    if (this.missingAuthentication.length > 0) {
      console.log("Missing authentication for: ", this.missingAuthentication);
      await this.loginAllMissingProviders(this.missingAuthentication);
    }
    else {
      console.log("User is authenticated");
      this.getConnectorConfiguration();
      this.configService.fetchAllConfigs();
      this.router.navigate(['/home'], { skipLocationChange: true });
    }
  }

  async loginAllMissingProviders(missingProviders: string[]) {

    return new Promise<boolean>(async (resolve, reject) => {
      try {
        if (missingProviders.includes("AzureAd")) {
          await this.loginProviderWithPopup("AzureAd");
          await this.getConnectorConfiguration();
        }
        if (missingProviders.includes("Acs")) {
          await this.loginProviderWithPopup("Acs");
        }
        if (missingProviders.includes("Connector")) {
          await this.loginProviderWithPopup("Connector");
        }
        this.authService.isAuthenticated$.pipe(take(1)).subscribe(isAuth => {
          if (isAuth.isAuthenticated) {
            this.configService.fetchAllConfigs();
            this.router.navigate(['/home'], { skipLocationChange: true });
            resolve(true);
          } else {
            reject(false);
          }
        });
      } catch (error) { 
        reject(error);
      }
    })
  }

  async loginAllProviders() {


    return new Promise<boolean>(async (resolve, reject) => {
      try {
        await this.loginProviderWithPopup("AzureAd");
        if (sessionStorage.getItem("CRMConf")) {
          await this.loginProviderWithPopup("Acs");
          await this.loginProviderWithPopup("Connector");
          this.configService.fetchAllConfigs();
          this.router.navigate(['/home'], { skipLocationChange: true });
          resolve(true);
        }
        else {
          this.getConnectorConfiguration();
          reject(false);
        }
      }
      catch (error) {
        console.error('Fehler beim Login:', error);
        reject(error);
      }
    })
  }

  async getConnectorConfiguration() {
    return new Promise(async (resolve, reject) => {
      var authConfigUrl = environment.apiUri + environment.connectorAuthQuery + environment.apiCode;
      if (window.location.href.startsWith("https://localhost")) {
        authConfigUrl = authConfigUrl + "&local"
      }

      if (sessionStorage.getItem("CRMConf")) {
        this.logger.log("Connector configuration already exists in session storage.");
        resolve(true);
        return;
      }

      this.http.get<any>(authConfigUrl).pipe(
        map((customConfig: any) => {
          return customConfig.data[0];
        })).subscribe(
          {
            next: result => {
              if (result != undefined) {
                sessionStorage.setItem("CRMConf", JSON.stringify(result));
                location.reload();
              }
              else {
                this.logger.warn("Missing connector configuration... Please add a configuration in the admin portal");
                sessionStorage.removeItem("CRMConf"); //remove the crm conf from localstorage
                resolve(false);
              }
            },
            error: (error) => {
              this.logger.error("Missing license");
              reject("Missing license");
              throw new Error("User is not licensed. Please contact your administrator.");
            }
          });
    })
  }

  async loginProviderWithPopup(provider: string) {
    console.log("Login: ", provider);
    return new Promise(async (resolve, reject) => {
      this.authService.authorizeWithPopUp(undefined, undefined, provider).subscribe(
        {
          next: (response) => {
            if (response.isAuthenticated) {
              console.log(`Login bei ${provider} erfolgreich`, response);
              resolve(response);
            }
            else {
              console.log(`Login bei ${provider} fehlgeschlagen: `, response.errorMessage);
              throw new Error(response.errorMessage);
            }
          },
          error: (error) => {
            console.error(`Login bei ${provider} fehlgeschlagen`, error);
            throw(error);
          }
        }
      )
    })
  }

  logoutAll() {
    this.authService.logoff('AzureAd').subscribe((result) => this.logger.log(result));
    this.authService.logoff('Acs').subscribe((result) => this.logger.log(result));
    this.authService.logoff('Connector').subscribe((result) => this.logger.log(result));
    sessionStorage.removeItem("CRMConf"); //remove the crm conf from localstorage
  }

  //#endregion

  getIdToken(): string {
    var idToken: string = "";
    this.authService.getIdToken("AzureAd").subscribe(
      token => {
        idToken = token;
      }); //we have to assign the new http header again (HttpHeaders will create a new header)
    return idToken;
  }

  sendTelemetryData() {
    try {
      var infos = JSON.parse(this.interop.getTelemetryFromClient());
      infos.environment = environment.type;
      infos["webVersion"] = environment.version;
      this.logger.log("ClientData: ", infos);//Call a function from interopClass in WPF C#
      this.logger.log("Client version: ", infos.appVersion.toString());

      this.http.post<tokenResponse>(environment.apiUri + environment.telemetryConfQuery + environment.apiCode, infos).subscribe(
        {
          next: result => {
            this.logger.log("Telemetry: ", result);
          },
          error: (error) => {
            this.logger.log("Telemetry ", error);
          }
        }
      );
    }
    catch (err) {
      this.logger.error("Error sending telemetry: ", err);
    }
  }

  //this function is used in the tokenrefresher of the acs call client. we have to create a promise of the acs token
  async getAcsTokenRefresher(): Promise<string> {
    this.logger.log("Acquire new token for ACS...");

    return new Promise<string>((resolve, reject) => {
      this.authService.forceRefreshSession(undefined, "AzureAd").pipe(
        concatMap(() => this.authService.forceRefreshSession(undefined, "Acs")),
        concatMap((result) => {
          if (!result.isAuthenticated) {
            throw new Error("Refresh session failed: not authenticated");
          }
          // beide Werte parallel holen, sobald wir wissen, dass die Session gültig ist
          return forkJoin({
            userData: this.authService.getUserData("AzureAd"),
            token: this.authService.getAccessToken("Acs")
          });
        })
      ).subscribe({
        next: ({ userData, token }) => {
          if (!userData) {
            this.logger.error("Missing userdata");
            reject("Missing userdata");
            location.reload();
            return;
          }

          const body = {
            teamsUserId: userData.oid,
            teamsUserToken: token
          };

          this.http.post<tokenResponse>(this.endPointUri, body).subscribe({
            next: (result) => {
              resolve(result.data.acsToken);
            },
            error: (response: HttpErrorResponse) => {
              this.logger.error("Cannot get ACS token: ", response);

              const errorCode = response?.error?.error?.errorCode;
              const errorMessage = response?.error?.error?.errorMessage;

              switch (errorCode) {
                case 2: // Tenant not active
                  this.logger.error(errorMessage);
                  reject(new Error(`Code ${errorCode} : ${errorMessage}`));
                  break;
                case 3: // User is not licensed
                  this.logger.error(errorMessage);
                  reject(new Error(`Code ${errorCode} : ${errorMessage}`));
                  break;
                default:
                  this.logger.error("Error ACS Token");
                  reject(new Error("Error ACS Token"));
              }
            }
          });
        },
        error: (error) => {
          this.logger.error("Forced refresh or user/token retrieval failed", error);
          reject(error);
        }
      });
    });
  }
}